import React, { useState, useRef } from 'react';
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  HStack,
  Badge,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Switch,
  Heading,
  Icon,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { FaPlus, FaEdit, FaTrash, FaCreditCard, FaBoxOpen } from 'react-icons/fa';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';

import {
  getPaymentGateways,
  deletePaymentGateway,
  updatePaymentGateway,
  getPaymentGatewayTypes,
} from '../services/paymentGatewayService';
import CustomModal from '../components/Modal';
import PaymentGatewayForm from '../components/forms/PaymentGatewayForm';

const TYPE_COLORS = {
  zarinpal: 'yellow.500',
  mellat: 'red.500',
  paystar: 'purple.500',
  torobpay: 'orange.500',
  snappay: 'green.500',
  digipay: 'pink.500',
};

export default function PaymentGateways() {
  const queryClient = useQueryClient();

  const bgPage = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.200');
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const theadBg = useColorModeValue('gray.50', 'gray.700');
  const searchBarBg = useColorModeValue('white', 'gray.800');
  const searchInputBg = useColorModeValue('gray.50', 'gray.900');
  const searchInputFocusBg = useColorModeValue('white', 'gray.800');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGateway, setEditingGateway] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const formRef = useRef(null);

  const {
    data: gatewaysData,
    isLoading,
    error,
    refetch,
  } = useQuery(['payment-gateways'], getPaymentGateways, {
    keepPreviousData: true,
    staleTime: 2 * 60 * 1000,
  });

  const { data: typesData } = useQuery(['payment-gateway-types'], getPaymentGatewayTypes, {
    staleTime: 10 * 60 * 1000,
  });

  const typeLabelMap = Object.fromEntries(
    (typesData?.data || []).map((t) => [t.type, t.label])
  );

  const deleteMutation = useMutation(deletePaymentGateway, {
    onSuccess: () => {
      toast.success('درگاه پرداخت با موفقیت حذف شد');
      queryClient.invalidateQueries(['payment-gateways']);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'خطا در حذف درگاه پرداخت');
    },
  });

  const handleDelete = (id) => {
    if (window.confirm('آیا از حذف این درگاه پرداخت اطمینان دارید؟')) {
      deleteMutation.mutate(id);
    }
  };

  const handleAdd = () => {
    setEditingGateway(null);
    setIsModalOpen(true);
  };

  const handleEdit = (gateway) => {
    setEditingGateway(gateway);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingGateway(null);
  };

  const handleSubmitForm = () => {
    if (formRef.current) formRef.current.handleSubmit();
  };

  const filtered =
    gatewaysData?.data?.filter((g) => {
      if (!searchTerm) return true;
      const q = searchTerm.toLowerCase();
      const label = (typeLabelMap[g.type] || g.type || '').toLowerCase();
      return (
        g.name?.toLowerCase().includes(q) ||
        g.type?.toLowerCase().includes(q) ||
        label.includes(q)
      );
    }) || [];

  if (isLoading) {
    return (
      <Flex direction="column" dir="rtl" minH="100vh" bg={bgPage} align="center" justify="center">
        <Box position="relative" mb={6}>
          <Spinner
            size="xl"
            thickness="4px"
            speed="0.65s"
            emptyColor="gray.200"
            color="blue.500"
            w="60px"
            h="60px"
          />
          <Icon
            as={FaCreditCard}
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            color="blue.500"
          />
        </Box>
        <Text fontSize="lg" fontWeight="bold" color={textColor}>
          در حال دریافت لیست درگاه‌های پرداخت...
        </Text>
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex direction="column" dir="rtl" minH="100vh" bg={bgPage} p={8}>
        <Alert status="error" borderRadius="2xl" boxShadow="sm" p={6}>
          <AlertIcon boxSize="24px" />
          <Box ml={4}>
            <AlertTitle fontSize="lg" mb={2}>
              خطا در بارگذاری داده‌ها!
            </AlertTitle>
            <AlertDescription color="gray.600">
              {error.message || 'مشکلی در ارتباط با سرور به وجود آمده است.'}
            </AlertDescription>
          </Box>
        </Alert>
        <Button mt={6} colorScheme="blue" alignSelf="start" onClick={() => refetch()}>
          تلاش مجدد
        </Button>
      </Flex>
    );
  }

  return (
    <>
      <Flex direction="column" dir="rtl" minH="100vh" bg={bgPage} pb={10}>
        <Box maxW="7xl" mx="auto" w="full" px={{ base: 4, md: 8 }} py={8}>
          <Flex justify="space-between" align="flex-end" mb={8} flexWrap="wrap" gap={4}>
            <Box>
              <Heading size="lg" color={textColor} letterSpacing="tight" mb={2}>
                مدیریت درگاه‌های پرداخت
              </Heading>
              <Text fontSize="sm" color={mutedColor}>
                زرین‌پال، ملت، پی‌استار، ترب‌پی، اسنپ‌پی و دیجی‌پی را پیکربندی کنید.
              </Text>
            </Box>
            <Button
              leftIcon={<FaPlus />}
              bgGradient="linear(to-r, blue.500, cyan.400)"
              color="white"
              borderRadius="xl"
              px={6}
              py={6}
              fontSize="md"
              fontWeight="bold"
              boxShadow="0 10px 20px -5px rgba(59,130,246,0.4)"
              _hover={{
                bgGradient: 'linear(to-r, blue.600, cyan.500)',
                transform: 'translateY(-2px)',
                boxShadow: '0 15px 25px -5px rgba(59,130,246,0.5)',
              }}
              _active={{ transform: 'translateY(0)' }}
              onClick={handleAdd}
            >
              افزودن درگاه پرداخت
            </Button>
          </Flex>

          <Box
            bg={cardBg}
            borderRadius="2xl"
            borderWidth="1px"
            borderColor={borderColor}
            boxShadow="sm"
            overflow="hidden"
          >
            <Flex p={5} borderBottomWidth="1px" borderColor={borderColor} bg={searchBarBg}>
              <InputGroup maxW="400px" size="md">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="جستجو با نام یا نوع درگاه..."
                  borderRadius="xl"
                  bg={searchInputBg}
                  border="none"
                  _focus={{ ring: 2, ringColor: 'blue.400', bg: searchInputFocusBg }}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Flex>

            <Box overflowX="auto">
              <Table variant="simple" size="md">
                <Thead bg={theadBg}>
                  <Tr>
                    <Th color={mutedColor} fontSize="xs" textTransform="none" py={4} borderColor={borderColor}>
                      نام درگاه
                    </Th>
                    <Th color={mutedColor} fontSize="xs" textTransform="none" py={4} borderColor={borderColor}>
                      نوع
                    </Th>
                    <Th color={mutedColor} fontSize="xs" textTransform="none" py={4} borderColor={borderColor}>
                      وضعیت
                    </Th>
                    <Th
                      color={mutedColor}
                      fontSize="xs"
                      textTransform="none"
                      py={4}
                      borderColor={borderColor}
                      textAlign="center"
                    >
                      عملیات
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filtered.length === 0 ? (
                    <Tr>
                      <Td colSpan={4} textAlign="center" py={16} borderBottom="none">
                        <Flex direction="column" align="center" justify="center">
                          <Icon as={FaBoxOpen} boxSize={12} color="gray.300" mb={4} />
                          <Text fontSize="lg" fontWeight="bold" color={textColor} mb={1}>
                            {searchTerm
                              ? 'درگاهی با این نام یافت نشد'
                              : 'هیچ درگاه پرداختی ثبت نشده است'}
                          </Text>
                          <Text fontSize="sm" color={mutedColor}>
                            برای شروع، روی دکمه «افزودن درگاه پرداخت» کلیک کنید.
                          </Text>
                        </Flex>
                      </Td>
                    </Tr>
                  ) : (
                    filtered.map((gateway) => (
                      <Tr key={gateway._id} _hover={{ bg: hoverBg }} transition="background 0.2s">
                        <Td borderColor={borderColor} py={4}>
                          <HStack spacing={3}>
                            <Flex
                              w="40px"
                              h="40px"
                              borderRadius="lg"
                              bg="blue.50"
                              color="blue.500"
                              align="center"
                              justify="center"
                            >
                              <Icon as={FaCreditCard} />
                            </Flex>
                            <Text fontSize="md" fontWeight="bold" color={textColor}>
                              {gateway.name}
                            </Text>
                          </HStack>
                        </Td>
                        <Td borderColor={borderColor}>
                          <Badge
                            bg={TYPE_COLORS[gateway.type] || 'gray.500'}
                            color="white"
                            fontSize="xs"
                            px={3}
                            py={1}
                            borderRadius="full"
                            shadow="sm"
                          >
                            {typeLabelMap[gateway.type] || gateway.type}
                          </Badge>
                        </Td>
                        <Td borderColor={borderColor}>
                          <Switch
                            isChecked={gateway.isActive !== false}
                            colorScheme="green"
                            size="md"
                            onChange={async (e) => {
                              try {
                                await updatePaymentGateway(gateway._id, {
                                  isActive: e.target.checked,
                                });
                                queryClient.invalidateQueries(['payment-gateways']);
                                toast.success('وضعیت با موفقیت بروزرسانی شد');
                              } catch (err) {
                                toast.error(
                                  err?.response?.data?.message || 'خطا در تغییر وضعیت'
                                );
                              }
                            }}
                          />
                        </Td>
                        <Td borderColor={borderColor} textAlign="center">
                          <HStack spacing={2} justify="center">
                            <Tooltip label="ویرایش درگاه" placement="top" hasArrow>
                              <IconButton
                                size="sm"
                                variant="ghost"
                                colorScheme="teal"
                                icon={<FaEdit />}
                                onClick={() => handleEdit(gateway)}
                                borderRadius="lg"
                              />
                            </Tooltip>
                            <Tooltip label="حذف درگاه" placement="top" hasArrow bg="red.500">
                              <IconButton
                                size="sm"
                                variant="ghost"
                                colorScheme="red"
                                icon={<FaTrash />}
                                onClick={() => handleDelete(gateway._id)}
                                borderRadius="lg"
                              />
                            </Tooltip>
                          </HStack>
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </Box>
          </Box>
        </Box>
      </Flex>

      <CustomModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingGateway ? 'ویرایش درگاه پرداخت' : 'ایجاد درگاه پرداخت جدید'}
        onSubmit={handleSubmitForm}
        submitText={editingGateway ? 'ذخیره تغییرات' : 'ثبت و ایجاد درگاه'}
      >
        <PaymentGatewayForm
          ref={formRef}
          gateway={editingGateway}
          onSuccess={() => {
            queryClient.invalidateQueries(['payment-gateways']);
            setIsModalOpen(false);
            setEditingGateway(null);
          }}
        />
      </CustomModal>
    </>
  );
}
